import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, ChevronDown, Check } from "lucide-react";
import { SUPPORTED_LANGS } from "../i18n";

/**
 * Compact language switcher button.
 * variant="light"   → for white/cream navbars
 * variant="dark"    → for dark navbars (footer / overlays)
 * variant="ghost"   → minimal (icon + 2-letter code)
 */
const LanguageSwitcher = ({ variant = "light", align = "right" }) => {
    const { i18n, t } = useTranslation();
    const [open, setOpen] = useState(false);
    const wrap = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const current = SUPPORTED_LANGS.find((l) => l.code === i18n.language)
        || SUPPORTED_LANGS.find((l) => l.code === (i18n.language || "").split("-")[0])
        || SUPPORTED_LANGS[0];

    const change = (code) => {
        i18n.changeLanguage(code);
        setOpen(false);
    };

    const styles = {
        light: {
            btn: "border border-cream-200 bg-white hover:border-blue-300 hover:text-blue-500 text-ink",
            menu: "bg-white border border-cream-200 shadow-lg",
            item: "hover:bg-cream-100 text-ink",
            itemActive: "bg-blue-50 text-blue-500",
        },
        dark: {
            btn: "border border-white/20 bg-white/10 hover:bg-white/15 text-white",
            menu: "bg-[#0f172a] border border-white/15 shadow-2xl",
            item: "hover:bg-white/10 text-white/85",
            itemActive: "bg-white/15 text-white",
        },
        ghost: {
            btn: "text-[var(--ink-soft,#52525b)] hover:text-[var(--ink,#0f172a)]",
            menu: "bg-white border border-[var(--border,#e7e1d0)] shadow-lg",
            item: "hover:bg-[var(--bg-surface,#f1ede3)] text-[var(--ink,#0f172a)]",
            itemActive: "bg-[var(--brand-soft,#eef2ff)] text-[var(--brand,#4f46e5)]",
        },
    }[variant];

    return (
        <div className="relative" ref={wrap} data-testid="lang-switcher">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label={t("common.language")}
                className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition ${styles.btn}`}
                data-testid="lang-switcher-btn"
            >
                <Globe className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{current.label}</span>
                <span className="sm:hidden uppercase tracking-wider">{current.code}</span>
                <ChevronDown className="w-3 h-3 opacity-70" />
            </button>
            {open && (
                <div
                    className={`absolute top-full mt-2 ${align === "right" ? "right-0" : "left-0"} z-[110] min-w-[180px] rounded-xl overflow-hidden ${styles.menu}`}
                    data-testid="lang-switcher-menu"
                >
                    <div className="py-1.5">
                        {SUPPORTED_LANGS.map((l) => {
                            const active = l.code === current.code;
                            return (
                                <button
                                    key={l.code}
                                    type="button"
                                    onClick={() => change(l.code)}
                                    className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-sm text-left transition ${
                                        active ? styles.itemActive : styles.item
                                    }`}
                                    data-testid={`lang-option-${l.code}`}
                                    dir={l.dir}
                                >
                                    <span className="text-base leading-none">{l.flag}</span>
                                    <span className="flex-1 font-medium">{l.label}</span>
                                    {active && <Check className="w-4 h-4 opacity-80" />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LanguageSwitcher;
